import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { InternalLinkService } from '@bling-fe/shared/services';
import { InternalLinkTitleService } from '../services/internal-link-title.service';

/**
 * Upgrades internal links in rendered post/content HTML from their generic
 * section label ("Gruppe", "Beitrag", "Event") to the resource's real name
 * ("Wohnbaupolitik", the post title, …).
 *
 * For every internal `<a>` in the host's [innerHTML], it asks InternalLinkService
 * whether the link points at a resolvable resource and still shows generic text
 * (so author-written link text is never touched), then resolves the name via
 * InternalLinkTitleService and writes it into the anchor. Resolution is per
 * viewer and silently falls back to the generic label for inaccessible or
 * deleted resources.
 *
 * The eligibility is derived from each anchor's href rather than data-*
 * attributes, because HTML sanitisation strips data-* but keeps href. The host
 * content can re-render (read-more expand/collapse swaps the HTML), so a
 * MutationObserver re-runs the pass whenever the subtree changes.
 */
@Directive({
  selector: '[flResolveLinkTitles]',
  standalone: false
})
export class ResolveLinkTitlesDirective implements AfterViewInit, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();
  private observer?: MutationObserver;
  // Anchors already handled (keyed by element) so we don't re-resolve on every
  // mutation; a WeakSet lets handled nodes be GC'd with the DOM.
  private readonly seen = new WeakSet<Element>();

  constructor(
    private host: ElementRef<HTMLElement>,
    private internalLinkService: InternalLinkService,
    private titleService: InternalLinkTitleService,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // Initial pass once the [innerHTML] has been rendered.
    this.resolveAll();

    // Re-resolve on DOM changes (e.g. read-more toggling the rendered HTML).
    // Runs outside Angular to avoid change-detection churn; the textContent
    // write is a leaf DOM mutation that needs no zone.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.resolveAll());
      this.observer.observe(this.host.nativeElement, {
        childList: true,
        subtree: true
      });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private resolveAll(): void {
    const anchors =
      this.host.nativeElement.querySelectorAll<HTMLAnchorElement>('a[href]');

    anchors.forEach(anchor => {
      if (this.seen.has(anchor)) return;

      const ref = this.internalLinkService.upgradableResourceRef(anchor);
      if (!ref) return;
      this.seen.add(anchor);

      this.titleService
        .resolve(ref.type, ref.id)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(title => {
          if (title) anchor.textContent = title;
        });
    });
  }
}
